import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import deleteSiteIngestion from "../http/deleteSiteIngestion";
import getReduxiControllerDevices, {
  ReduxiControllerDevicesResponse,
} from "../http/getReduxiControllerDevices";
import getSimStatus, {
  SimStatusResponse,
} from "../http/getSimStatus";
import postImportSoracomSim, {
  ImportSoracomSimRequest,
  ImportSoracomSimResponse,
} from "../http/postImportSoracomSim";
import postRegisterSoracomSim, {
  RegisterSoracomSimRequest,
  RegisterSoracomSimResponse,
} from "../http/postRegisterSoracomSim";
import getSiteIngestion, {
  SiteIngestionResponse,
} from "../http/getSiteIngestion";
import getSiteReadingsTables, {
  SiteReadingsTablesResponse,
} from "../http/getSiteReadingsTables";
import postCreateSiteReadingsTables, {
  CreateSiteReadingsTablesResponse,
} from "../http/postCreateSiteReadingsTables";
import postPopulateDemoActuals, {
  PopulateDemoActualsRequest,
  PopulateDemoActualsResponse,
} from "../http/postPopulateDemoActuals";
import putSiteIngestion, {
  PutSiteIngestionRequest,
} from "../http/putSiteIngestion";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

interface UseSiteSetupActionsParams {
  siteId: string;
  esysUuids: string[];
  enabled?: boolean;
}

const useSiteSetupActions = ({
  siteId,
  esysUuids,
  enabled,
}: UseSiteSetupActionsParams) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const fetchReadingsTables: UseQueryResult<
    SiteReadingsTablesResponse,
    unknown
  > = useQuery(
    ["site", siteId, "readings_tables"],
    async () => {
      return getSiteReadingsTables(
        await getAuthTokenBlocking(getToken, relogin),
        siteId
      );
    },
    {
      enabled: enabled ?? true,
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );

  const createReadingsTables = useMutation(
    async (): Promise<CreateSiteReadingsTablesResponse> => {
      return postCreateSiteReadingsTables(
        await getAuthTokenBlocking(getToken, relogin),
        siteId
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["site", siteId, "readings_tables"]);
      },
    }
  );

  const populateDemoActuals = useMutation(
    async (
      request: PopulateDemoActualsRequest
    ): Promise<PopulateDemoActualsResponse> => {
      return postPopulateDemoActuals(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        request
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["site", siteId, "readings_tables"]);
      },
    }
  );

  const ingestionResults = useQueries({
    queries: esysUuids.map((uuid) => ({
      queryKey: ["site", siteId, "ingestion", uuid],
      queryFn: async () => {
        return getSiteIngestion(
          await getAuthTokenBlocking(getToken, relogin),
          siteId,
          uuid
        );
      },
      enabled: enabled ?? true,
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  const ingestionByEsys: Record<
    string,
    UseQueryResult<SiteIngestionResponse | null, unknown>
  > = {};
  esysUuids.forEach((uuid, i) => {
    ingestionByEsys[uuid] = ingestionResults[i];
  });

  const updateIngestion = useMutation(
    async (
      request: PutSiteIngestionRequest
    ): Promise<SiteIngestionResponse> => {
      return putSiteIngestion(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        request
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["site", siteId, "ingestion"]);
      },
    }
  );

  const deleteIngestion = useMutation(
    async (esysUuid: string) => {
      return deleteSiteIngestion(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        esysUuid
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["site", siteId, "ingestion"]);
      },
    }
  );

  const simStatusResults = useQueries({
    queries: esysUuids.map((uuid) => ({
      queryKey: ["site", siteId, "sim_status", uuid],
      queryFn: async () => {
        try {
          return await getSimStatus(
            await getAuthTokenBlocking(getToken, relogin),
            siteId,
            uuid
          );
        } catch {
          return null;
        }
      },
      enabled: enabled ?? true,
      staleTime: 60 * 1000,
      retry: false,
    })),
  });

  const simStatusByEsys: Record<
    string,
    UseQueryResult<SimStatusResponse | null, unknown>
  > = {};
  esysUuids.forEach((uuid, i) => {
    simStatusByEsys[uuid] = simStatusResults[i];
  });

  const discoverReduxiDevices = useMutation(
    async (
      controllerId: string
    ): Promise<ReduxiControllerDevicesResponse> => {
      return getReduxiControllerDevices(
        await getAuthTokenBlocking(getToken, relogin),
        controllerId
      );
    }
  );

  const registerSoracomSim = useMutation(
    async (
      request: RegisterSoracomSimRequest & {
        siteId: string;
        esysUuid: string;
      }
    ): Promise<RegisterSoracomSimResponse> => {
      const { siteId: reqSiteId, esysUuid, ...body } = request;
      return postRegisterSoracomSim(
        await getAuthTokenBlocking(getToken, relogin),
        reqSiteId,
        esysUuid,
        body
      );
    }
  );

  const importSoracomSim = useMutation(
    async (
      request: ImportSoracomSimRequest & {
        siteId: string;
        esysUuid: string;
      }
    ): Promise<ImportSoracomSimResponse> => {
      const { siteId: reqSiteId, esysUuid, ...body } = request;
      return postImportSoracomSim(
        await getAuthTokenBlocking(getToken, relogin),
        reqSiteId,
        esysUuid,
        body
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["site", siteId, "ingestion"]);
        queryClient.invalidateQueries(["site", siteId, "sim_status"]);
      },
    }
  );

  return {
    fetchReadingsTables,
    createReadingsTables,
    populateDemoActuals,
    ingestionByEsys,
    updateIngestion,
    deleteIngestion,
    simStatusByEsys,
    discoverReduxiDevices,
    registerSoracomSim,
    importSoracomSim,
  };
};

export default useSiteSetupActions;
